<template>
  <section class="candidate application questionnaire">
    <v-card flat width="100%">
      <v-card-text class="subheading pb-0">
        <div v-if="questionnaire.answerSet.submittedAt">
          <v-icon class="text-vertical-middle" :color="themes.candidate.light.success">
            mdi-calendar
          </v-icon>
          Submitted on {{ new Date(questionnaire.answerSet.submittedAt) | date('d MMM yyy') }}
        </div>
        <div v-else>
          <v-icon left class="text-vertical-middle" :color="themes.candidate.light.warning">
            mdi-comment-question
          </v-icon>
          Answers have <strong>NOT</strong> been submitted
        </div>
      </v-card-text>

      <v-card-text>
        <span
          v-for="prompt in sortedQuestions"
          :key="`question-${prompt.question.id}`"
          :is="questionComponent(prompt.question)"
          :submitted="submitted && !editing"
          :value="prompt"
          @saving="saving = true"
          @saved="saving = false"
        />
      </v-card-text>

      <v-card-actions v-if="!submitted">
        <v-spacer />
        <v-btn flat v-if="saving">Autosaving…</v-btn>

        <v-btn
          @click="submitting = true"
          :color="themes.candidate.light.primaryActionButton"
          :disabled="!completeable || saving"
        >
          Submit
        </v-btn>
        <v-dialog max-width="600px" v-model="submitting">
          <v-card>
            <v-card-title :class="themes.candidate.light.dialogTitle">
              <div>
                <div class="headline">Submit your answers?</div>
                <div class="subheading" :class="themes.candidate.light.cardSubtitle">
                  Are you sure you want to submit them now?
                </div>
              </div>
            </v-card-title>

            <v-alert
              :value="!completeable || saving"
              :color="themes.candidate.light.error"
              type="error"
            >
              Review incomplete questions
            </v-alert>

            <v-card-text>
              The recruiting team will not be able to see your answers or files
              until you submit them.
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn flat :color="themes.candidate.light.muted" @click="submitting = false">
                Cancel
              </v-btn>
              <v-btn
                @click="submit"
                :color="themes.candidate.light.primaryActionButton"
                :disabled="!completeable || saving"
              >
                Confirm
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-actions>
      <v-card-actions v-else-if="!editing">
        <v-spacer />
        <v-btn :color="themes.candidate.light.primaryActionButton"
          @click="editing = true">
          Edit
        </v-btn>
        <v-spacer />
      </v-card-actions>
      <v-card-actions v-else>
        <v-spacer />
        <v-btn disabled
          :color="themes.candidate.light.primaryActionButton"
        >{{ saving ? 'Saving' : 'Saved' }}</v-btn>
        <v-btn v-if="editing"
          :color="themes.candidate.light.primaryActionButton"
          @click="editing = false">
          Done
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </section>
</template>

<script>
// Components
import ChoiceAnswer from '@/components/candidate/questionnaire/ChoiceAnswer'
import FileAnswer from '@/components/candidate/questionnaire/FileAnswer'
import TextAnswer from '@/components/candidate/questionnaire/TextAnswer'

// Helpers
import _ from 'lodash'

// Vue Component
export default {
  components: { ChoiceAnswer, FileAnswer, TextAnswer },

  created() {
    this.setCompleteable()
  },

  props: {
    questionnaire: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editing: false,
      saving: false,
      submitting: false,
      completeable: false,
    }
  },
  computed: {
    submitted() {
      return !!(this.questionnaire.answerSet && this.questionnaire.answerSet.submittedAt)
    },
    sortedQuestions() {
      if (!_.get(this, 'questionnaire.questions.length')) return []

      const questions = JSON.parse(JSON.stringify(this.questionnaire.questions))
      return questions.sort((a, b) => a.question.position - b.question.position)
    },
  },
  watch: {
    saving(val) {
      if (!val) {
        this.$store
          .dispatch('candidate/applications/fetch', this.$route.params.id)
          .then(this.setCompleteable)
      }
    },
  },
  methods: {
    setCompleteable() {
      this.completeable = this.questionnaire.questions.every(prompt => {
        if (prompt.question.type === 'text' || prompt.question.type === 'choice') {
          return !!(
            prompt.question.optional ||
            (prompt.answer.id && prompt.answer.content && prompt.answer.content.length)
          )
        }
        if (prompt.question.type === 'file') {
          return !!(prompt.question.optional || (prompt.answer.id && prompt.answer.attachment))
        }
        return false
      })
    },
    submit() {
      if (this.completeable && this.questionnaire.answerSet) {
        this.$http.patch(`candidate/answer_sets/${this.questionnaire.answerSet.id}`).then(() => {
          this.submitting = false
          this.$store.dispatch('candidate/applications/fetch', Vue.$route.params.id)
        })
      }
    },
    questionComponent(question) {
      return {
        text: 'TextAnswer',
        file: 'FileAnswer',
        choice: 'ChoiceAnswer',
      }[question.type]
    },
  },
}
</script>
